'use client';

import { FORM_EVENTS, MARKETING_EVENTS, NAVIGATION_EVENTS } from '@repo/analytics';
import { stripLocalePrefix } from '@repo/web-chrome/lib/path';
import { useEffect } from 'react';
import { recordInstallIntent } from '../record-install-intent';
import { trackEvent } from '../track-event';

function resolveAppStore(href: string): 'ios' | 'android' | null {
  const clean = (href.split('?')[0] ?? href).split('#')[0] ?? href;
  if (clean === '/ios' || clean.endsWith('/ios')) {
    return 'ios';
  }
  if (clean === '/android' || clean.endsWith('/android')) {
    return 'android';
  }
  return null;
}

function trackAnchorClick(anchor: HTMLAnchorElement): void {
  const href = anchor.getAttribute('href') ?? '';
  const store = resolveAppStore(href);
  if (store) {
    trackEvent(MARKETING_EVENTS.appStoreClicked, {
      store,
      source: stripLocalePrefix(window.location.pathname),
    });
    recordInstallIntent(store);
    return;
  }
  if (href.startsWith('mailto:')) {
    trackEvent(FORM_EVENTS.contactSubmitted, { channel: 'email' });
    return;
  }
  const label = (anchor.getAttribute('data-track-label') ?? anchor.textContent ?? '')
    .trim()
    .slice(0, 80);
  const group = anchor.getAttribute('data-track-group') ?? '';
  trackEvent(NAVIGATION_EVENTS.linkClicked, { href, label, group });
}

export function useLinkClickTracking(): void {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const handler = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest('a');
      if (!anchor) {
        return;
      }
      trackAnchorClick(anchor);
    };
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, []);
}
